// Generated from products/analytics/sdk/devToolsSignal.ts; run analytics generate:site-runtime.
const SUSPECTED_GAP_PX = 170;
const POLL_MS = 500;
const MAX_GAP_PX = 32_768;
const DEBOUNCE_MS = 1_000;

export interface DevToolsSignal {
  confidence: "heuristic";
  method: "window-dimensions";
  widthGap: number;
  heightGap: number;
}

export interface DevToolsWindowSurface {
  readonly innerWidth: number;
  readonly outerWidth: number;
  readonly innerHeight: number;
  readonly outerHeight: number;
  addEventListener(type: "resize", listener: () => void): void;
  removeEventListener(type: "resize", listener: () => void): void;
}

export interface DevToolsSignalEnvironment {
  window?: DevToolsWindowSurface | null | undefined;
  now?: (() => number) | undefined;
  setTimeout?: ((callback: () => void, delayMs: number) => unknown) | undefined;
  clearTimeout?: ((handle: unknown) => void) | undefined;
}

interface Sample extends DevToolsSignal {
  suspected: boolean;
}

function defaultWindow(): DevToolsWindowSurface | null {
  try {
    return typeof window === "undefined" ? null : window;
  } catch {
    return null;
  }
}

function defaultSetTimeout(callback: () => void, delayMs: number): unknown {
  return globalThis.setTimeout(callback, delayMs);
}

function defaultClearTimeout(handle: unknown): void {
  globalThis.clearTimeout(handle as ReturnType<typeof setTimeout>);
}

function readSample(target: DevToolsWindowSurface): Sample | null {
  try {
    const innerWidth = target.innerWidth;
    const outerWidth = target.outerWidth;
    const innerHeight = target.innerHeight;
    const outerHeight = target.outerHeight;
    const dimensions = [innerWidth, outerWidth, innerHeight, outerHeight];
    if (dimensions.some((value) => !Number.isFinite(value) || value <= 0)) return null;
    if (outerWidth < innerWidth || outerHeight < innerHeight) return null;

    const widthGap = Math.min(MAX_GAP_PX, Math.max(0, Math.trunc(outerWidth - innerWidth)));
    const heightGap = Math.min(MAX_GAP_PX, Math.max(0, Math.trunc(outerHeight - innerHeight)));
    const widthSuspected = widthGap > SUSPECTED_GAP_PX;
    const heightSuspected = heightGap > SUSPECTED_GAP_PX;
    if (widthSuspected && heightSuspected) return null;
    return {
      confidence: "heuristic",
      method: "window-dimensions",
      widthGap,
      heightGap,
      suspected: widthSuspected || heightSuspected,
    };
  } catch {
    return null;
  }
}

/**
 * Observe a weak DevTools-open hint from window dimensions. Docking, resizing,
 * and browser chrome can produce false positives; this signal is heuristic only.
 */
export function observeDevToolsSignal(
  onSuspected: (signal: DevToolsSignal) => void,
  environment?: DevToolsSignalEnvironment | undefined,
): () => void {
  let target: DevToolsWindowSurface | null;
  let schedule: (callback: () => void, delayMs: number) => unknown;
  let cancel: (handle: unknown) => void;
  let now: () => number;
  try {
    target =
      environment && "window" in environment ? (environment.window ?? null) : defaultWindow();
    schedule = environment?.setTimeout ?? defaultSetTimeout;
    cancel = environment?.clearTimeout ?? defaultClearTimeout;
    now = environment?.now ?? (() => globalThis.performance.now());
  } catch {
    return () => undefined;
  }
  if (!target || typeof onSuspected !== "function") return () => undefined;

  let disposed = false;
  let timer: unknown;
  let hasTimer = false;
  let previousSuspected = false;
  let candidate: boolean | null = null;
  let candidateSince = 0;

  const clearPending = () => {
    if (!hasTimer) return;
    const pending = timer;
    timer = undefined;
    hasTimer = false;
    try {
      cancel(pending);
    } catch {
      /* Best-effort host cleanup. */
    }
  };

  // Invalid and dual-axis samples cannot confirm a close/reopen transition.
  const observe = () => {
    let timestamp: number;
    try {
      timestamp = now();
    } catch {
      candidate = null;
      return;
    }
    if (!Number.isFinite(timestamp)) {
      candidate = null;
      return;
    }
    const sample = readSample(target as DevToolsWindowSurface);
    if (!sample) {
      candidate = null;
      candidateSince = 0;
      return;
    }
    if (candidate !== sample.suspected) {
      candidate = sample.suspected;
      candidateSince = timestamp;
    }
    if (timestamp < candidateSince) {
      candidateSince = timestamp;
      return;
    }
    if (timestamp - candidateSince < DEBOUNCE_MS || previousSuspected === candidate) return;
    previousSuspected = candidate;
    if (candidate) {
      try {
        onSuspected({
          confidence: sample.confidence,
          method: sample.method,
          widthGap: sample.widthGap,
          heightGap: sample.heightGap,
        });
      } catch {
        /* Analytics must not affect the host. */
      }
    }
  };

  const poll = () => {
    if (disposed) return;
    try {
      timer = schedule(() => {
        if (disposed) return;
        timer = undefined;
        hasTimer = false;
        observe();
        poll();
      }, POLL_MS);
      hasTimer = true;
    } catch {
      timer = undefined;
      hasTimer = false;
    }
  };

  const onResize = () => {
    if (!disposed) observe();
  };

  try {
    target.addEventListener("resize", onResize);
  } catch {
    return () => undefined;
  }
  observe();
  poll();

  return () => {
    if (disposed) return;
    disposed = true;
    clearPending();
    try {
      target?.removeEventListener("resize", onResize);
    } catch {
      // Cleanup remains safe even if a host surface throws.
    }
  };
}
